import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { products, productCategories } from "@/mocks/products";

const AllProductsSection = () => {
  const [category, setCategory] = useState("All");
  const [warehouse, setWarehouse] = useState("All");

  const filtered = useMemo(() => {
    let list = [...products].sort((a, b) => b.sold - a.sold);
    if (category !== "All") list = list.filter((p) => p.category === category);
    if (warehouse !== "All")
      list = list.filter((p) => p.warehouse === warehouse || p.warehouse === "Both");
    return list;
  }, [category, warehouse]);

  return (
    <section id="all-products" className="py-16 bg-[#fdf8f4]">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[#C41E3A] text-sm font-semibold uppercase tracking-wider mb-1">
              Verified Factory and Farm Suppliers Made in USA
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight">
              All Products
            </h2>
            <p className="text-gray-500 text-sm mt-2">
              {filtered.length} products shipped to USA &amp; Canada from verified factory suppliers
            </p>
          </div>
          <Link
            to="/products"
            className="flex items-center gap-2 bg-[#C41E3A] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap self-start md:self-auto"
          >
            View All Products <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8 items-start sm:items-center">
          {/* Category pills */}
          <div className="flex flex-wrap gap-2 flex-1">
            {productCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  category === cat
                    ? "bg-[#C41E3A] text-white"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-[#C41E3A]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          {/* Warehouse toggle */}
          <div className="flex gap-1 flex-shrink-0">
            {[
              { key: "All", label: "All" },
              { key: "USA", label: "🇺🇸 USA" },
              { key: "Canada", label: "🇨🇦 Canada" },
            ].map((w) => (
              <button
                key={w.key}
                onClick={() => setWarehouse(w.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  warehouse === w.key
                    ? "bg-[#1a0a0a] text-white"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-gray-400"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {/* Product Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-5xl"></i>
            </div>
            <p className="text-lg font-semibold">No products found</p>
            <p className="text-sm mt-1">Try a different filter</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filtered.map((product) => (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-[#C41E3A] transition-all group cursor-pointer block"
              >
                {/* Image */}
                <div className="relative h-44 overflow-hidden bg-gray-50">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  {product.badge && (
                    <div className="absolute top-2 left-2 bg-[#C41E3A] text-white text-[10px] font-bold px-2 py-0.5 rounded">
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 text-sm">
                    {product.warehouse === "USA"
                      ? "🇺🇸"
                      : product.warehouse === "Canada"
                      ? "🇨🇦"
                      : "🇺🇸🇨🇦"}
                  </div>
                </div>
                {/* Info */}
                <div className="p-3">
                  <div className="text-[10px] text-gray-400 mb-1 truncate">{product.supplier}</div>
                  <h3 className="font-semibold text-gray-900 text-xs leading-tight mb-2 line-clamp-2">
                    {product.name}
                  </h3>
                  <div className="flex items-center gap-1 mb-2">
                    <i className="ri-star-fill text-[#D4AF37] text-xs"></i>
                    <span className="text-xs text-gray-600 font-medium">{product.rating}</span>
                    <span className="text-[10px] text-gray-400">
                      ({product.reviews.toLocaleString()})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-extrabold text-[#C41E3A]">${product.price}</span>
                    <span className="text-xs text-gray-400 line-through">
                      ${product.originalPrice}
                    </span>
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1">
                    Min. {product.minOrder} {product.unit}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="text-center mt-10">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 border-2 border-[#C41E3A] text-[#C41E3A] px-8 py-3 rounded-full text-sm font-semibold hover:bg-[#C41E3A] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            Browse All {products.length} Products <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AllProductsSection;
